import { Box, Typography, TextField, Button, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom'; // useSearchParams to get URL query params
import { toast } from 'react-toastify';
import COLORS from '../../../constants/colors';
import baseURL from '../user/config';
import Footer from './Reusbale/Footer';
import Header from '../../Reusable/Header';


const SetPassword = () => {
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [email, setEmail] = useState(null);

    const navigate = useNavigate();

    // 1. Get token and email from the URL when the component loads
    useEffect(() => {
        const tokenParam = searchParams.get('token');
        const emailParam = searchParams.get('email');

        if (tokenParam && emailParam) {
            setToken(tokenParam);
            setEmail(emailParam);
        } else {
            // Redirect if token or email are missing
            toast.error('Invalid or missing link parameters.');
            navigate('/user/login');
        }
    }, [searchParams, navigate]);

    // 2. Handle the form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Basic validation
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters long.');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match.');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${baseURL}/org/set-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ token, email, newPassword: password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to set password. Link may be invalid or expired.');
            }

            toast.success('Password set successfully! You can now log in.');
            setTimeout(() => navigate('/user/login'), 2000);

        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Header />
            <Box sx={{ py: 3, px: 3, maxWidth: 400, margin: '0 auto', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color={COLORS.primary} mb={2}>
                    Set Your Password
                </Typography>
                <Typography variant="body1" color={COLORS.text} mb={4}>
                    Please create a new password to activate your account.
                </Typography>

                <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
                    {/* New Password Field */}
                    <TextField
                        name="password"
                        label="New Password"
                        variant="outlined"
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '1rem', '& input': { padding: '14px' } } }}
                        InputLabelProps={{ style: { color: COLORS.primary, fontSize: '1rem' } }}
                        InputProps={{
                            style: { color: COLORS.text },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon icon={faLock} style={{ color: COLORS.primary }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Confirm Password Field */}
                    <TextField
                        name="confirmPassword"
                        label="Confirm New Password"
                        variant="outlined"
                        fullWidth
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '1rem', '& input': { padding: '14px' } } }}
                        InputLabelProps={{ style: { color: COLORS.primary, fontSize: '1rem' } }}
                        InputProps={{
                            style: { color: COLORS.text },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon icon={faLock} style={{ color: COLORS.primary }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                            backgroundColor: COLORS.primary,
                            color: COLORS.white,
                            borderRadius: '10px',
                            py: 1.2,
                            fontSize: '1rem',
                            textTransform: 'none',
                            boxShadow: 0,
                            '&:hover': { backgroundColor: COLORS.secondary },
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Set Password'}
                    </Button>
                </Box>
            </Box>
            <Footer />
        </Box>
    );
};

export default SetPassword;